import { Extension } from '@tiptap/core';
import { Plugin } from '@tiptap/pm/state';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

/**
 * DocumentTableRowResize — row-HEIGHT resizing for document-mode tables.
 *
 * `Table.configure({ resizable: true })` only loads prosemirror-tables'
 * `columnResizing` plugin, which gives every cell a drag handle on its
 * RIGHT edge (a 5 px hit band). There is no row equivalent, so this
 * extension adds one without replacing any of the existing table nodes:
 *
 * 1. `addGlobalAttributes()` attaches a `rowHeight` attribute to the
 *    already-registered `tableRow` node. `null` means "never resized":
 *    nothing is rendered, so existing documents keep rendering
 *    byte-identically. A resized row renders as
 *    `<tr data-row-height="120" style="height: 120px">` — the data
 *    attribute makes the value survive HTML round-trips and the inline
 *    height behaves as a MINIMUM (CSS `height` on `<tr>` never clips
 *    content), so a row whose content is taller simply grows.
 *
 * 2. `addProseMirrorPlugins()` implements the interaction:
 *      • mousedown (button 0, editable) within `ROW_HEIGHT_EDGE_PX` of a
 *        cell's BOTTOM edge starts a row-height drag (live `mousemove`
 *        updates, `mouseup`/`blur` end it). The row that gets resized is
 *        the one whose rendered bottom border is under the pointer, so a
 *        cell merged across rows (`rowspan`) resizes the LAST row of its
 *        span — the row the grabbed border actually belongs to,
 *      • a mousemove hover affordance shows a `row-resize` cursor and a
 *        2 px accent line on the cell under the pointer.
 *
 * Interactions that must keep working:
 *  • COLUMN resizing — prosemirror-tables' handle is active within 5 px
 *    of a cell's LEFT or RIGHT edge (dragging a cell's left edge resizes
 *    the previous column). Both vertical bands are therefore reserved
 *    here (`ROW_HEIGHT_COLUMN_GUARD_PX` = 10 px > 5 px), so a mousedown
 *    in either band is never consumed by this plugin.
 *  • Cell selection / right-click menu — only `event.button === 0` is
 *    ever inspected, and `DocumentTableContext`'s button-2 path is left
 *    completely alone.
 *  • Typing, keyboard navigation and the `+table=3x4` shortcut — no
 *    schema node, keymap or input rule is added or replaced.
 *
 * Plugin ordering: this extension is registered right after `TableCell`
 * in the editor's `extensions` array. TipTap builds the ProseMirror
 * plugin list by reversing the extension list and re-sorting by
 * priority, so this plugin lands BEFORE `columnResizing` /
 * `tableEditing`. Starting a row drag therefore returns `true` from
 * `mousedown`, which keeps prosemirror-tables from simultaneously
 * starting a cell-selection drag under the row drag. Whenever no row
 * drag starts the handler returns `false`, so every other mousedown
 * behaves exactly as before.
 */

/** Smallest row height (px) a drag can produce. */
export const ROW_HEIGHT_MIN = 28;
/** Largest row height (px) a drag can produce. */
export const ROW_HEIGHT_MAX = 1200;
/** Grab band (px) above a cell's bottom border that starts a row drag. */
export const ROW_HEIGHT_EDGE_PX = 5;
/**
 * Horizontal dead zone (px) kept clear on BOTH sides of a cell so
 * prosemirror-tables' `columnResizing` handle (5 px band on either
 * vertical edge) keeps working exactly as today.
 */
export const ROW_HEIGHT_COLUMN_GUARD_PX = 10;
/** Attribute rendered on `<tr>` so the height survives HTML round-trips. */
export const ROW_HEIGHT_ATTRIBUTE = 'data-row-height';
/** Class toggled on the hovered cell (styled in index.css). */
export const ROW_HEIGHT_HOVER_CLASS = 'doc-row-resize-hover';

/** Unit-less or `px` pixel value, e.g. `120`, `120px`, `12.5px`. */
const PX_VALUE_PATTERN = /^(\d+(?:\.\d+)?)\s*(?:px)?$/i;
const TABLE_ROW_NODE_NAME = 'tableRow';
const ROW_RESIZE_CURSOR = 'row-resize';

/* ------------------------------------------------------------------ *
 * Pure helpers (exported so the maths can be probed without a browser)
 * ------------------------------------------------------------------ */

/**
 * Clamp a pixel height into `[ROW_HEIGHT_MIN, ROW_HEIGHT_MAX]` and round
 * it to a whole pixel. Non-finite input (NaN/Infinity/null/undefined)
 * falls back to `ROW_HEIGHT_MIN`.
 */
export function clampRowHeight(value: number | null | undefined): number {
    if (typeof value !== 'number' || !Number.isFinite(value)) return ROW_HEIGHT_MIN;
    return Math.min(ROW_HEIGHT_MAX, Math.max(ROW_HEIGHT_MIN, Math.round(value)));
}

/**
 * Height for a drag: `startHeight + deltaY`, clamped. A missing start
 * height (attribute unset and the row was not measurable) starts from
 * `ROW_HEIGHT_MIN`; a non-finite delta is treated as no movement.
 */
export function resolveRowHeight(
    startHeight: number | null | undefined,
    deltaY: number | null | undefined,
): number {
    const base = typeof startHeight === 'number' && Number.isFinite(startHeight) ? startHeight : ROW_HEIGHT_MIN;
    const delta = typeof deltaY === 'number' && Number.isFinite(deltaY) ? deltaY : 0;
    return clampRowHeight(base + delta);
}

/**
 * Normalise any stored/parsed value into a positive integer number of
 * pixels, or `null` when there is nothing usable (unset attribute,
 * `auto`, percentages, garbage).
 */
export function normalizeRowHeight(value: unknown): number | null {
    if (typeof value === 'number') {
        return Number.isFinite(value) && value > 0 ? Math.round(value) : null;
    }
    if (typeof value === 'string') {
        const match = PX_VALUE_PATTERN.exec(value.trim());
        const raw = match?.[1];
        if (raw === undefined) return null;
        const parsed = Number.parseFloat(raw);
        return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
    }
    return null;
}

/**
 * `parseHTML` for the `rowHeight` attribute: prefers the explicit
 * `data-row-height` attribute and falls back to an inline pixel height
 * (e.g. content pasted from another editor).
 */
export function parseRowHeight(element: HTMLElement): number | null {
    if (!element || typeof element.getAttribute !== 'function') return null;
    const fromAttribute = normalizeRowHeight(element.getAttribute(ROW_HEIGHT_ATTRIBUTE));
    if (fromAttribute !== null) return fromAttribute;
    const inlineHeight = element.style && typeof element.style.height === 'string' ? element.style.height : '';
    return normalizeRowHeight(inlineHeight);
}

/**
 * `renderHTML` for the `rowHeight` attribute. Returning `{}` for an
 * unset/invalid height is what keeps untouched rows attribute-free.
 */
export function renderRowHeightAttributes(value: unknown): Record<string, string> {
    const height = normalizeRowHeight(value);
    if (height === null) return {};
    return {
        [ROW_HEIGHT_ATTRIBUTE]: String(height),
        style: `height: ${height}px`,
    };
}

/* ------------------------------------------------------------------ *
 * DOM / ProseMirror helpers
 * ------------------------------------------------------------------ */

/** The `<td>`/`<th>` an event target sits in, if it belongs to this view. */
function cellFromTarget(view: EditorView, target: EventTarget | null): HTMLElement | null {
    const element = target as HTMLElement | null;
    if (!element || typeof element.closest !== 'function') return null;
    const cell = element.closest('td,th') as HTMLElement | null;
    if (!cell || !view.dom.contains(cell)) return null;
    return cell;
}

/**
 * True only for the bottom-edge grab band of a cell, with both vertical
 * edges excluded so TipTap's column handles keep their hit zone.
 */
function isBottomEdgeZone(cell: HTMLElement, clientX: number, clientY: number): boolean {
    const rect = cell.getBoundingClientRect();
    if (!rect || rect.height <= 0) return false;
    if (rect.right - clientX <= ROW_HEIGHT_COLUMN_GUARD_PX) return false;
    if (clientX - rect.left <= ROW_HEIGHT_COLUMN_GUARD_PX) return false;
    // Inside the cell's bottom `ROW_HEIGHT_EDGE_PX` band.
    return rect.bottom - clientY <= ROW_HEIGHT_EDGE_PX;
}

/**
 * The `<tr>` a grab should resize: the row whose rendered BOTTOM border
 * sits under the pointer (nearest one wins). For an ordinary cell that is
 * the cell's own row. For a cell merged across rows (`rowspan`) the
 * border the user grabbed is drawn at the bottom of the LAST row of the
 * span, so that is the row we must resize — `cell.closest('tr')` would
 * wrongly return the first row of the span.
 */
function rowElementAtPointer(cell: HTMLElement, clientY: number): HTMLElement | null {
    const fallback = cell.closest('tr');
    const table = cell.closest('table') as HTMLTableElement | null;
    const rows = table && table.rows ? Array.from(table.rows) : [];
    let best: HTMLElement | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (const row of rows) {
        const rect = row.getBoundingClientRect();
        if (rect.height <= 0) continue;
        const distance = Math.abs(rect.bottom - clientY);
        if (distance <= ROW_HEIGHT_EDGE_PX && distance < bestDistance) {
            best = row;
            bestDistance = distance;
        }
    }
    return best ?? fallback;
}

/**
 * Position + node of the `tableRow` containing a `<td>`/`<th>`/`<tr>`,
 * derived from `view.posAtDOM(element, 0)` and by walking up the resolved
 * node stack.
 */
function findRowAt(view: EditorView, element: HTMLElement): { pos: number; node: ProseMirrorNode } | null {
    let domPos: number;
    try {
        domPos = view.posAtDOM(element, 0);
    } catch {
        return null;
    }
    const doc = view.state.doc;
    const pos = Math.max(0, Math.min(domPos, doc.content.size));
    const $pos = doc.resolve(pos);
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
        const node = $pos.node(depth);
        if (node.type.name === TABLE_ROW_NODE_NAME) {
            return { pos: $pos.before(depth), node };
        }
    }
    return null;
}

/** Current height of a row: its attribute if set, else what it renders at. */
function currentRowHeight(row: ProseMirrorNode, rowElement: HTMLElement | null): number {
    const stored = normalizeRowHeight(row.attrs.rowHeight);
    if (stored !== null) return stored;
    const rendered = rowElement?.getBoundingClientRect().height ?? 0;
    return Number.isFinite(rendered) && rendered > 0 ? Math.round(rendered) : ROW_HEIGHT_MIN;
}

type RowResizeDrag = {
    view: EditorView;
    /** Cell the drag started on; used to re-resolve the row each frame. */
    cell: HTMLElement;
    rowPos: number;
    startY: number;
    startHeight: number;
    lastHeight: number;
    previousBodyCursor: string;
    previousBodyUserSelect: string;
};

/* ------------------------------------------------------------------ *
 * Extension
 * ------------------------------------------------------------------ */

export const DocumentTableRowResize = Extension.create({
    name: 'documentTableRowResize',

    addGlobalAttributes() {
        return [
            {
                types: [TABLE_ROW_NODE_NAME],
                attributes: {
                    // `default: null` + an empty `renderHTML` payload for
                    // unset values means rows the user never resized get
                    // no attribute at all (no diff noise, no markup drift).
                    rowHeight: {
                        default: null,
                        parseHTML: (element: HTMLElement) => parseRowHeight(element),
                        renderHTML: (attributes: Record<string, unknown>) =>
                            renderRowHeightAttributes(attributes.rowHeight),
                    },
                },
            },
        ];
    },

    addProseMirrorPlugins() {
        const editor = this.editor;

        let drag: RowResizeDrag | null = null;
        let hoverCell: HTMLElement | null = null;
        let frame = 0;
        let pendingClientY = 0;

        /** Show the affordance on one cell (and take it off the previous). */
        const clearHover = () => {
            if (!hoverCell) return;
            hoverCell.classList.remove(ROW_HEIGHT_HOVER_CLASS);
            if (hoverCell.style.cursor === ROW_RESIZE_CURSOR) hoverCell.style.removeProperty('cursor');
            hoverCell = null;
        };

        const markHover = (cell: HTMLElement) => {
            if (hoverCell === cell) return;
            clearHover();
            hoverCell = cell;
            cell.classList.add(ROW_HEIGHT_HOVER_CLASS);
            cell.style.cursor = ROW_RESIZE_CURSOR;
        };

        /**
         * Write the live height into the document. The row position is
         * re-derived from the DOM cell on every frame so a concurrent
         * remote edit cannot make us write at a stale position; the node
         * type is re-checked before dispatching for the same reason.
         */
        const applyHeight = (view: EditorView, clientY: number) => {
            if (!drag) return;
            const next = resolveRowHeight(drag.startHeight, clientY - drag.startY);
            if (next === drag.lastHeight) return;

            if (drag.cell.isConnected) {
                const resolved = findRowAt(view, drag.cell);
                if (resolved) drag.rowPos = resolved.pos;
            }
            const row = view.state.doc.nodeAt(drag.rowPos);
            if (!row || row.type.name !== TABLE_ROW_NODE_NAME) return;

            drag.lastHeight = next;
            view.dispatch(
                view.state.tr.setNodeMarkup(drag.rowPos, undefined, { ...row.attrs, rowHeight: next }),
            );
        };

        const scheduleHeight = (clientY: number) => {
            pendingClientY = clientY;
            if (frame) return;
            frame = window.requestAnimationFrame(() => {
                frame = 0;
                const active = drag;
                if (active) applyHeight(active.view, pendingClientY);
            });
        };

        const finishDrag = () => {
            const active = drag;
            if (!active) return;
            drag = null;
            if (frame) {
                window.cancelAnimationFrame(frame);
                frame = 0;
            }
            window.removeEventListener('mousemove', onWindowMouseMove, true);
            window.removeEventListener('mouseup', finishDrag, true);
            window.removeEventListener('blur', finishDrag, true);
            document.body.style.cursor = active.previousBodyCursor;
            document.body.style.userSelect = active.previousBodyUserSelect;
            clearHover();
        };

        /** Window-level move: keeps the drag alive outside the editor. */
        const onWindowMouseMove = (event: MouseEvent) => {
            if (!drag) return;
            // Safety net: a mouseup we never received (released outside
            // the window) shows up as "no button pressed".
            if (event.buttons === 0) {
                finishDrag();
                return;
            }
            scheduleHeight(event.clientY);
        };

        const handleMouseDown = (view: EditorView, event: MouseEvent): boolean => {
            if (event.button !== 0 || drag) return false;
            if (!editor.isEditable || editor.isDestroyed) return false;
            const cell = cellFromTarget(view, event.target);
            if (!cell) return false;
            if (!isBottomEdgeZone(cell, event.clientX, event.clientY)) return false;

            const rowElement = rowElementAtPointer(cell, event.clientY);
            if (!rowElement) return false;
            // Resolve the `<tr>` ITSELF rather than the cell: for a cell
            // merged across rows the grabbed border belongs to the last
            // row of the span. `findRowAt(cell)` stays as a fallback.
            const resolved = findRowAt(view, rowElement) ?? findRowAt(view, cell);
            if (!resolved) return false;
            const startHeight = currentRowHeight(resolved.node, rowElement);

            drag = {
                view,
                cell,
                rowPos: resolved.pos,
                startY: event.clientY,
                startHeight,
                lastHeight: startHeight,
                previousBodyCursor: document.body.style.cursor,
                previousBodyUserSelect: document.body.style.userSelect,
            };

            // No text selection / focus change while the handle is dragged.
            event.preventDefault();
            document.body.style.cursor = ROW_RESIZE_CURSOR;
            document.body.style.userSelect = 'none';
            window.addEventListener('mousemove', onWindowMouseMove, true);
            window.addEventListener('mouseup', finishDrag, true);
            window.addEventListener('blur', finishDrag, true);
            clearHover();
            return true;
        };

        const handleMouseMove = (view: EditorView, event: MouseEvent): boolean => {
            // The drag itself is driven by the window listener.
            if (drag || !editor.isEditable || editor.isDestroyed) return false;
            const cell = cellFromTarget(view, event.target);
            if (!cell || !isBottomEdgeZone(cell, event.clientX, event.clientY)) {
                clearHover();
                return false;
            }
            markHover(cell);
            return false;
        };

        const handleMouseLeave = (): boolean => {
            if (!drag) clearHover();
            return false;
        };

        return [
            new Plugin({
                props: {
                    handleDOMEvents: {
                        mousedown: handleMouseDown,
                        mousemove: handleMouseMove,
                        mouseleave: handleMouseLeave,
                    },
                },
                // Tear the window listeners down with the editor so an
                // unmount mid-drag cannot leave a stuck body cursor.
                view: () => ({
                    destroy: () => {
                        finishDrag();
                        clearHover();
                    },
                }),
            }),
        ];
    },
});

export default DocumentTableRowResize;
